import * as React from "react";
import { Trash2, ShieldAlert } from "lucide-react";
import type { SelectionAction } from "../../../ui/components/Table";
import type { ButtonColor } from "../../../ui/components/Button/Button.types";
import * as productsApi from "../../lib/api/products";

export interface BuildProductSelectionActionsOptions {
  accessToken: string;
  confirm: (title: string, body: string, confirmLabel?: string, confirmColor?: ButtonColor) => Promise<boolean>;
  onMutationComplete?: () => void;
  onError?: (message: string) => void;
  isSuperAdmin?: boolean;
}

export function buildProductSelectionActions(
  opts: BuildProductSelectionActionsOptions,
): SelectionAction[] {
  const { accessToken, confirm, onMutationComplete, onError, isSuperAdmin } = opts;

  const actions: SelectionAction[] = [
    {
      icon: <Trash2 size={14} />,
      label: "Decommission",
      danger: true,
      onClick: async (ids, names) => {
        const title = ids.length === 1 && names?.[0]
          ? `Decommission ${names[0]}?`
          : `Decommission ${ids.length} product${ids.length !== 1 ? 's' : ''}?`
        const ok = await confirm(
          title,
          "Selected products will be marked as decommissioned and removed from active views. This can be reversed by editing the product status.",
          "Decommission",
        );
        if (!ok) return;
        try {
          await Promise.all(ids.map((id) => productsApi.deleteProduct(accessToken, id)));
          onMutationComplete?.();
        } catch (err) {
          onError?.(err instanceof Error ? err.message : "Decommission failed");
        }
      },
    },
  ];

  if (isSuperAdmin) {
    actions.push({
      icon: <ShieldAlert size={14} />,
      label: "Hard delete",
      danger: true,
      onClick: async (ids, names) => {
        const title = ids.length === 1 && names?.[0]
          ? `Permanently delete ${names[0]}?`
          : `Permanently delete ${ids.length} product${ids.length !== 1 ? 's' : ''}?`
        const ok = await confirm(
          title,
          `This will permanently remove the selected product(s) and all associated components from the database. This cannot be undone.`,
          "Delete permanently",
        );
        if (!ok) return;
        try {
          await Promise.all(ids.map((id) => productsApi.hardDeleteProduct(accessToken, id)));
          onMutationComplete?.();
        } catch (err) {
          onError?.(err instanceof Error ? err.message : "Hard delete failed");
        }
      },
    });
  }

  return actions;
}
